import React, { useEffect, useRef, useState } from "react";
import styles from './request.module.scss'
import LayoutFull from "../layouts/layoutFull/LayoutFull";
import Breadcrumb, { BreadcrumbLayer } from "../components/Breadcrumb";
import { connect } from "react-redux";
import ImgCDN from "../components/ImgCDN";
import Link from "next/link";
import {
    DEBUG,
} from "../settings/global";
import Input from "../components/Input";
import ReCaptcha from "../components/ReCaptcha";
import { createFetch } from "../helpers/fetch/FetchRequest";
import { dispatchNotification } from "../layouts/app/components/notificationSystem/notifications";
import Loading from "../components/Loading";
import Button from "../components/ripple/Button";
import { addModalLayer, wipeModalState } from "../redux/actions/ModalActions";
import { store } from "../redux/store";
import getPages from "../helpers/data/get/pages";
import SeoApiHandler from "../helpers/seo/SeoApiHandler";
import TextPageLayout from "../layouts/pages/article/TextPageLayout";
import { CardItem } from "../components/card/Card";
import { NAV_REQUEST_STACK, NAV_STACK_LIST } from "../settings/navigation";
import { API_CUSTOM_REQUEST_NODE } from "../settings/endpoints/app";

export async function getStaticProps() {
    return {
        props: {seo: await getPages(NAV_REQUEST_STACK)}
    }
}

function RequestStack({seo, dispatch, ...props}) {

    let captchaRef = useRef();

    const [inputs, updateInputs] = useState({message: '', apps: '', email: ''});
    const [loading, setLoading] = useState(false);
    const [captcha, setCaptcha] = useState(null);
    const [errors, updateErrors] = useState({});

    const resetCaptcha = () => {
        setCaptcha(null);
        if (!!captchaRef && !!captchaRef.current) captchaRef.current.reset();
    }

    useEffect(() => {
        if (!!props.user && !!props.user.email) updateInputs({...inputs, ...{email: props.user.email}})
    }, [props.user]);

    useEffect(() => {
        if (!!captcha) sendRequest();
    }, [captcha]);

    useEffect(() => {
        return () => {store.dispatch(wipeModalState())};
    }, []);

    let title = 'Application Stack Request';

    const onChange = e => {
        const {name, value} = e.target;
        updateInputs({...inputs, ...{[name]: value}});
    };

    const onCaptchaChange = (value) => {
        setCaptcha(value);
    };

    const sendRequest = () => {
        setLoading(true);
        createFetch({
            URL: API_CUSTOM_REQUEST_NODE,
            method: 'POST',
            body: {...inputs, ...{reCaptcha: captcha}},
            onSuccess: response => {
                resetCaptcha();
                setTimeout(() => {
                    store.dispatch(wipeModalState());
                }, 200);
                setLoading(false);
                dispatchNotification({
                    message: 'Your message has been sent. Thank you for your interest!',
                    type: 'success',
                    url: API_CUSTOM_REQUEST_NODE,
                    requestTitle: title + 'request',
                    responseContent: response,
                    status: status,
                });
                updateInputs({message: '', apps: '', email: ''});
                setCaptcha(null);
                updateErrors({});
            },
            onError: response => {
                resetCaptcha();
                setTimeout(() => {
                    store.dispatch(wipeModalState());
                }, 200);
                setLoading(false);
                let {error, responseBody} = response;
                let {status} = error;
                let message = 'Something went wrong. Please, try again later';

                if (status === 400) {
                    message = 'Some of the fields are incorrect. Please, recheck them and try again';
                    if (responseBody?._error?.message === 'Captcha ERROR. Please prove you are not a robot') {
                        message = 'Captcha ERROR. Please prove you are not a robot';
                    } else if (typeof responseBody?._error?.message === 'object') {
                        updateErrors(responseBody?._error?.message)
                    }
                }

                dispatchNotification({
                    message: message,
                    type: 'error',
                    url: API_CUSTOM_REQUEST_NODE,
                    requestTitle: title + 'request',
                    responseContent: responseBody?.['_error'],
                    status: status,
                });
            },
        })
    }

    const onSubmit = (e) => {
        e.preventDefault();
        store.dispatch(addModalLayer({
            component: (
                <ReCaptcha
                    getRef={el => captchaRef = el}
                    onErrored={data => DEBUG ? console.log(data) : () => {}}
                    onChange={onCaptchaChange}
                />
            ),
        }))
    };

    const getCategoryLink = (category) => `?tagsCriterion=category&descendant=true&tags=${category}`;

    return (
        <LayoutFull>
            <SeoApiHandler data={seo}/>

            <TextPageLayout
                breadcrumb={
                    <Breadcrumb inverse>
                        <BreadcrumbLayer link='/' name='Home'/>
                        <BreadcrumbLayer name={title}/>
                    </Breadcrumb>
                }
                title={title}
            >
                <div>
                    {loading && <Loading type='component-cover'/>}
                    <div>
                        <p className={styles.cometary}>
                            Apply today and we will select the most appropriate application stack for you or create
                            a brand new one based on your requirements.
                        </p>
                        <div className={styles.offer}>
                            <p className={styles.info}>
                                For example, a small smartphone company needs
                                an <Link href={NAV_STACK_LIST + getCategoryLink('E-commerce')} target='_blank'>
                                online store
                            </Link>
                                , <Link href={NAV_STACK_LIST + getCategoryLink('CRM')} target='_blank'>
                                CRM
                            </Link> to work with offers and online requests,
                                an <b>online chat</b>, and a <b>mailing server</b>.
                            </p>
                            <p className={styles.info}>For this we offer:</p>
                            <div className={styles['offer__chain']}>
                                <div className={styles['offer__chain__item-container']}>
                                    <ImgCDN src='/img/icons/appList/opencart-logo.svg' alt='Opencart logo'/>
                                    <p>Opencart</p>
                                </div>
                                <p className={styles['offer__chain__connection']}>+</p>
                                <div className={styles['offer__chain__item-container']}>
                                    <ImgCDN src='/img/icons/appList/odoo-logo.svg' alt='Odoo logo'/>
                                    <p>Odoo CRM</p>
                                </div>
                                <p className={styles['offer__chain__connection']}>+</p>
                                <div className={styles['offer__chain__item-container']}>
                                    <ImgCDN src='/img/partners/CloudIcons/own.svg' alt='SMTP server icon'/>
                                    <p>SMTP server</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.container}>
                        <h2>Ready to deal with us?</h2>
                        <p>
                            Fill in the near form. Our specialists will evaluate your request and create a unique offer
                            for you!
                        </p>
                        <p>Also, we provide technical support within two weeks.</p>
                        <div className={styles.container__content}>
                            <CardItem>
                                <form className={styles["form"]} onSubmit={onSubmit}>
                                    <Input
                                        name="apps"
                                        value={inputs.apps}
                                        onChange={onChange}
                                        error={errors.apps}
                                        isValid={!errors.apps ? undefined : false}
                                        placeholder='Please enter web application names you would like to include in stack'
                                        ariaLabel="Application names"
                                        required
                                    />
                                    <Input
                                        name="email"
                                        value={inputs.email}
                                        onChange={onChange}
                                        error={errors.email}
                                        isValid={!errors.email ? undefined : false}
                                        placeholder='Enter your e-mail'
                                        ariaLabel="Enter your e-mail"
                                        required
                                    />
                                    <Input
                                        name="message"
                                        type='textarea'
                                        value={inputs.message}
                                        onChange={onChange}
                                        error={errors.message}
                                        isValid={!errors.message ? undefined : false}
                                        placeholder='Describe your needs or requirements'
                                        ariaLabel="Describe your needs or requirements"
                                        visualVariables={{
                                            minHeight: '10rem',
                                            maxHeight: '30rem',
                                        }}
                                        required
                                    />
                                    <div className={styles["form__submit"]}>
                                        <Button
                                            title='Send Request'
                                            iconRight='far fa-paper-plane'
                                            type='submit'
                                            visuals={{height: '4rem'}}
                                            disabled={!!loading}
                                        />
                                    </div>
                                </form>
                            </CardItem>
                        </div>
                    </div>
                </div>
            </TextPageLayout>
        </LayoutFull>
    );
}

let mapStateToProps = state => ({
    user: state.userData.user,
})

export default connect(mapStateToProps)(RequestStack)